import React, {ReactNode} from 'react';
import {Box} from '../box';
import {VisibilityBox} from '../box/visibility-box';
import {FrameworkImage} from '../image-renderer';
import {UnstyledHtmlAnchor} from '../link-renderer/unstyled-html-anchor';
import {HorizontalDivider} from '../divider/horizontal-divider';
import {VerticalDivider} from '../divider/vertical-divider';
import {InstagramIcon, TwitterIcon, YouTubeIcon} from '../icons';
import {Typography} from '../typography';

export interface ProfileHeaderProps {
  image?: string;
  artistName?: string | null | any;
  instagramHandle?: string | null;
  twitterHandle?: string | null;
  youtubeHandle?: string | null;
  scrollComponent?: ReactNode;
  isStudioPage?: boolean;
}

export const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  image,
  artistName,
  instagramHandle,
  twitterHandle,
  youtubeHandle,
  scrollComponent,
  isStudioPage,
}: ProfileHeaderProps) => {
  const socialInfo = [];
  if (instagramHandle) {
    socialInfo.push({
      id: 'instagram',
      Icon: InstagramIcon,
      handle: instagramHandle,
      link: `https://instagram.com/${instagramHandle}`,
    });
  }
  if (twitterHandle) {
    socialInfo.push({
      id: 'twitter',
      Icon: TwitterIcon,
      handle: twitterHandle,
      link: `https://twitter.com/${twitterHandle}`,
    });
  }
  if (youtubeHandle) {
    socialInfo.push({
      id: 'youtube',
      Icon: YouTubeIcon,
      handle: youtubeHandle,
      link: `https://youtube.com/${youtubeHandle}`,
    });
  }

  return (
    <Box>
      {!isStudioPage && <HorizontalDivider />}
      <Box
        position="absolute"
        top={['-86px', '-124px']}
        left={['md', 'lg']}
        width={['172px', '208px']}
        height={['172px', '208px']}
        borderColor="outlines"
        borderWidth={isStudioPage ? 'zero' : 'one'}
        borderStyle="solid"
        borderRadius="circle"
        backgroundColor="surfaceDark"
        overflow="hidden"
      >
        {image ? (
          <FrameworkImage
            src={image}
            alt={artistName || ''}
            layout="fill"
            objectFit="contain"
          />
        ) : null}
      </Box>
      {scrollComponent ? (
        <Box
          marginTop={['twentyOne', 'zero']}
          css={{
            'mix-blend-mode': 'exclusion',
          }}
        >
          <>{scrollComponent}</>
        </Box>
      ) : null}
      {!isStudioPage && <HorizontalDivider />}
      <Box flex="1" flexDirection={['column', 'row']}>
        {socialInfo.map(({id, Icon, handle, link}, idx) => {
          const isLast = idx === socialInfo.length - 1;
          return (
            <Box key={id} flex="1">
              <Box flex="1" flexDirection="row" justifyContent="space-between">
                <UnstyledHtmlAnchor
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Box flexDirection="row" paddingY="md" pl="lg">
                    <Icon color="outlines" size={18} />
                    <Box pl="s">
                      <Typography color="textLow">{`@${handle}`}</Typography>
                    </Box>
                  </Box>
                </UnstyledHtmlAnchor>
                {!isLast && (
                  <VisibilityBox show={[false, true]}>
                    <VerticalDivider />
                  </VisibilityBox>
                )}
              </Box>
              {!isLast && (
                <VisibilityBox show={[true, false]}>
                  <HorizontalDivider />
                </VisibilityBox>
              )}
            </Box>
          );
        })}
      </Box>
      {socialInfo.length ? <HorizontalDivider /> : null}
    </Box>
  );
};
